import { useCallback, useEffect, useRef, useState } from 'react'

import {
  landingScrollTop,
  sectionDomId,
  sectionHeadingId,
  spySection,
  type QuoteSectionKey,
} from '@/features/quotes/sectionSpy'

/* The browser half of the quote page's scroll-spy (S-3, L-2). The rule itself
 * — which section is current — is `spySection`, pure and unit-tested; this
 * file only measures and feeds it, and moves the page when a link is pressed.
 *
 * ── Where the scrolling happens ─────────────────────────────────────────────
 *
 * Not the window. AppShell's `<main>` is the scroll container, so the listener
 * goes on the nearest scrollable ancestor of the bar, found from an ELEMENT the
 * caller hands over (`attach`). A callback ref held in state rather than a
 * ref object, because the page renders a skeleton first: the bar mounts a
 * render later, and an effect keyed on a ref object would have run once,
 * against nothing, and never again.
 *
 * ── Why a click pins the answer ─────────────────────────────────────────────
 *
 * A smooth scroll to «Условия» passes through «Маршрут» and «Расчёт» on the
 * way, and a spy that followed it would walk the bar's marker across every
 * link in between — the opposite of the press the reader just made. So a
 * pressed link is current from the press until the scroll ends (`scrollend`),
 * or until a timeout for a browser that never fires it, or for a scroll that
 * never started because the section was already in place.
 *
 * ── Why not `scrollIntoView` ───────────────────────────────────────────────
 *
 * It scrolls every scrollable ancestor, and the DOCUMENT is one: the shell
 * overflows the viewport by the top bar's height, so a section press also
 * scrolled <html> by 60px and dragged the top bar and the sidebar up with it
 * (found in Chrome; jsdom has no layout to show it). A press scrolls the spy's
 * own container and nothing else, to `landingScrollTop`, measured against the
 * bar's RENDERED height — the same height the spy counts as covered.
 *
 * Nothing here writes the URL: the page is one sheet, and a scroll position is
 * not an address.
 */

/** How long a pressed link stays current if `scrollend` never comes. */
const PIN_FALLBACK_MS = 1000

function scrollParent(element: HTMLElement | null): HTMLElement | null {
  let node = element?.parentElement ?? null
  while (node) {
    const { overflowY } = window.getComputedStyle(node)
    if (overflowY === 'auto' || overflowY === 'scroll') return node
    node = node.parentElement
  }
  return null
}

function prefersReducedMotion(): boolean {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

export interface SectionSpy {
  /** The section the bar marks as current. */
  active: QuoteSectionKey
  /** Scroll a section into view and move focus to its heading. */
  go: (key: QuoteSectionKey) => void
  /** Attach to an element INSIDE the scroll container — the bar. */
  attach: (element: HTMLElement | null) => void
}

/** The strip the stuck bar covers: its rendered height, or the declared one
 *  where nothing is laid out (jsdom). */
function barHeight(anchor: HTMLElement, fallback: number): number {
  return anchor.getBoundingClientRect().height || fallback
}

/**
 * @param keys   the sections on the page, in page order. Pass a stable array
 *               (a module constant): it keys the listener.
 * @param offset the sticky bar's declared height, in pixels — used only where
 *               the bar has no rendered height to measure.
 */
export function useSectionSpy(
  keys: readonly QuoteSectionKey[],
  offset: number,
): SectionSpy {
  const [current, setCurrent] = useState<QuoteSectionKey>(keys[0] ?? 'client')
  const [anchor, setAnchor] = useState<HTMLElement | null>(null)
  const pinned = useRef(false)
  const pinTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  useEffect(() => {
    if (!anchor) return
    const container = scrollParent(anchor)
    const target: HTMLElement | Window = container ?? window
    let frame = 0

    const measure = () => {
      frame = 0
      if (pinned.current) return
      const originTop = container ? container.getBoundingClientRect().top : 0
      const scrollTop = container ? container.scrollTop : window.scrollY
      const sections = keys.flatMap((key) => {
        const element = document.getElementById(sectionDomId(key))
        return element
          ? [{ key, top: element.getBoundingClientRect().top - originTop + scrollTop }]
          : []
      })
      const next = spySection(
        sections,
        {
          scrollTop,
          viewportHeight: container ? container.clientHeight : window.innerHeight,
          scrollHeight: container
            ? container.scrollHeight
            : document.documentElement.scrollHeight,
        },
        barHeight(anchor, offset),
      )
      if (next) setCurrent(next)
    }

    const onScroll = () => {
      if (!frame) frame = window.requestAnimationFrame(measure)
    }
    const onScrollEnd = () => {
      pinned.current = false
      clearTimeout(pinTimer.current)
      measure()
    }

    target.addEventListener('scroll', onScroll, { passive: true })
    target.addEventListener('scrollend', onScrollEnd)
    // Once on attach: a page re-rendered mid-scroll starts on the right link.
    onScroll()

    return () => {
      target.removeEventListener('scroll', onScroll)
      target.removeEventListener('scrollend', onScrollEnd)
      if (frame) window.cancelAnimationFrame(frame)
    }
  }, [anchor, keys, offset])

  useEffect(() => () => clearTimeout(pinTimer.current), [])

  const go = useCallback((key: QuoteSectionKey) => {
    const section = document.getElementById(sectionDomId(key))
    if (!section) return
    const container = anchor ? scrollParent(anchor) : null

    pinned.current = true
    setCurrent(key)
    clearTimeout(pinTimer.current)
    pinTimer.current = setTimeout(() => {
      pinned.current = false
    }, PIN_FALLBACK_MS)

    // Only the container scrolls — never the document (see the header note).
    // Instant for a reader who asked the system for less motion.
    const top = landingScrollTop({
      sectionTop: section.getBoundingClientRect().top,
      containerTop: container ? container.getBoundingClientRect().top : 0,
      scrollTop: container ? container.scrollTop : window.scrollY,
      barHeight: anchor ? barHeight(anchor, offset) : offset,
    })
    const behavior: ScrollBehavior = prefersReducedMotion() ? 'auto' : 'smooth'
    if (container) container.scrollTo({ top, behavior })
    else window.scrollTo({ top, behavior })
    // The heading, so the keyboard and a screen reader land where the eye
    // does. `preventScroll`: the scroll above is already on its way, and a
    // focus scroll would cut it short.
    document.getElementById(sectionHeadingId(key))?.focus({ preventScroll: true })
  }, [anchor, offset])

  return { active: current, go, attach: setAnchor }
}
